import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../utils/ErrorUtils";
import { bonusAdminBody } from "./bonusAdminBody";
import { LIST_CAMPAIGNS_QUERY_KEY } from "./useListCampaigns";

import type { CampaignIn, CampaignOut } from "../../components/Promotions/DynamicBonuses/types";

type StoredToken = { AccessToken?: string };

/**
 * Create variables. Prefer `{ campaign, activate }` for new call sites.
 * A bare `CampaignIn` is still accepted and auto-activates (Review → Create).
 */
export type CreateCampaignVariables =
  | CampaignIn
  | {
      campaign: CampaignIn;
      /** When false, leave the campaign in draft (Save as Draft). Default true. */
      activate?: boolean;
    };

const normalizeCreateCampaignVariables = (
  vars: CreateCampaignVariables,
): { campaign: CampaignIn; activate: boolean } => {
  if (
    vars &&
    typeof vars === "object" &&
    "campaign" in vars &&
    vars.campaign &&
    typeof vars.campaign === "object" &&
    "name" in vars.campaign &&
    "steps" in vars.campaign
  ) {
    return {
      campaign: vars.campaign,
      activate: vars.activate !== false,
    };
  }
  return { campaign: vars as CampaignIn, activate: true };
};

async function postCampaign(
  payload: CampaignIn,
  activate: boolean,
): Promise<CampaignOut> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const response = await instance.post(
    API_ROUTES.CREATE_DYNAMIC_BONUS_CAMPAIGN,
    payload,
  );
  const created = bonusAdminBody<CampaignOut>(response);
  if (!created) {
    throw new Error("Campaign creation returned no data");
  }

  // Campaigns are created in `draft`. Review → Create auto-activates so they
  // start immediately. Save as Draft skips activation. Creation is still
  // considered successful even if activation fails.
  if (activate && created.campaign_id) {
    try {
      const activateUrl = API_ROUTES.ACTIVATE_DYNAMIC_BONUS_CAMPAIGN.replace(
        "{campaign_id}",
        created.campaign_id,
      );
      await instance.post(activateUrl);
      return { ...created, status: "active" };
    } catch {
      return created;
    }
  }
  return created;
}

export const useCreateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation<CampaignOut, Error, CreateCampaignVariables>({
    mutationFn: (vars) => {
      const { campaign, activate } = normalizeCreateCampaignVariables(vars);
      return postCampaign(campaign, activate);
    },
    onSuccess: (campaign, vars) => {
      const { activate } = normalizeCreateCampaignVariables(vars);
      queryClient.invalidateQueries({ queryKey: LIST_CAMPAIGNS_QUERY_KEY });
      if (!activate) {
        message.success(AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_SAVED_AS_DRAFT);
        return;
      }
      if (campaign?.status === "active") {
        message.success(AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_CREATED_AND_STARTED);
      } else {
        message.warning(AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_CREATED_NOT_STARTED);
      }
    },
    onError: (error) => {
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    },
  });
};
